import { useCallback, useEffect, useRef, useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'

import { navigate, routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { FIND_APPLICANT_COMPANY_FOR_SWITCH } from 'src/graphql/applicants.graphql'
import { UPDATE_CURRENT_EMPLOYEE_ID } from 'src/graphql/users.graphql'

export interface ApplicantCompanySwitchTarget {
  applicantId: string
  applicantFirstName: string
  applicantLastName: string
  companyId: string
  companyName: string
  employeeId: string
}

export const useApplicantCompanySwitch = (applicantId: string) => {
  const { currentUser } = useAuth()
  const [switchTarget, setSwitchTarget] =
    useState<ApplicantCompanySwitchTarget | null>(null)
  const resolvedApplicantIdRef = useRef<string | null>(null)

  useEffect(() => {
    resolvedApplicantIdRef.current = null
  }, [applicantId])

  const goToNotFound = useCallback(() => {
    navigate(routes.applicantNotFound())
  }, [])

  const [findApplicantCompany] = useLazyQuery(
    FIND_APPLICANT_COMPANY_FOR_SWITCH,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const target = data?.findApplicantCompanyForSwitch
        if (target) {
          setSwitchTarget(target)
        } else {
          goToNotFound()
        }
      },
      onError: goToNotFound,
    }
  )

  const [updateCurrentEmployeeId, { loading: isSwitching }] = useMutation(
    UPDATE_CURRENT_EMPLOYEE_ID,
    {
      onCompleted: (data) => {
        if (!data?.updateCurrentEmployeeId) {
          toast('Unable to switch companies. Please try again.', 'error')
          return
        }
        window.location.reload()
      },
      onError: () => {
        toast('Unable to switch companies. Please try again.', 'error')
      },
    }
  )

  const handleApplicantNotFound = useCallback(() => {
    if (!currentUser?.isInternalAdmin) {
      goToNotFound()
      return
    }
    if (resolvedApplicantIdRef.current === applicantId) {
      return
    }
    resolvedApplicantIdRef.current = applicantId
    findApplicantCompany({ variables: { applicantId } })
  }, [currentUser, applicantId, findApplicantCompany, goToNotFound])

  const confirmSwitch = useCallback(() => {
    if (!switchTarget) return
    updateCurrentEmployeeId({
      variables: { input: { currentEmployeeId: switchTarget.employeeId } },
    })
  }, [switchTarget, updateCurrentEmployeeId])

  const cancelSwitch = useCallback(() => {
    setSwitchTarget(null)
    goToNotFound()
  }, [goToNotFound])

  return {
    handleApplicantNotFound,
    switchTarget,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  }
}

export default useApplicantCompanySwitch
